import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SpinnerService as Spinner } from '../../shared/components/spinner/spinner.service';
import { environment } from '../../../environments/environment';
import { ProfilePersonalDataService as ProfilePersonalData } from '../profile/profile-personal-data/profile-personal-data.service';
import { BackofficeAccessService } from '../../shared/components/sidebar/sidebar.service';

export interface AuthTokenResponse {
  token_type?: string;
  expires_in?: number;
  access_token: string;
  refresh_token?: string;
  user_id?: number | string;
  user?: {
    id?: number | string;
    name: string;
    email: string;
  };
}
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API_URL = environment.apiUrl;

  private accessTokenSignal = signal<string | null>(localStorage.getItem('access_token'));


  private emailSignal = signal<string | null>(localStorage.getItem('user_email'));

  private userIdSignal = signal<string | null>(localStorage.getItem('user_id'));

  accessToken = this.accessTokenSignal.asReadonly();
  email = this.emailSignal.asReadonly();
  user_id = this.userIdSignal.asReadonly();

  constructor(
    private http: HttpClient,
    private spinner: Spinner,
    private profilePersonalData: ProfilePersonalData,
    private backofficeAccess: BackofficeAccessService,
  ) {}

  login(email: string, password: string) {
    this.spinner.open({
      title: 'Iniciando sesión',
      message: 'Validando tus credenciales, por favor esperá.',
    });

    return this.http
      .post<AuthTokenResponse>(`${this.API_URL}/backoffice/login`, {
        email: email,
        password,
        client_id: environment.auth.clientId,
        client_secret: environment.auth.clientSecret,
      })
      .pipe(
        tap((resp) => {
          // The backoffice endpoint returns `user_id`; older responses used
          // a nested `user` object. Accept both shapes so a successful 200
          // response is not turned into a client-side login error.
          const userId = resp.user_id ?? resp.user?.id;
          const userEmail = resp.user?.email ?? email;

          localStorage.setItem('access_token', resp.access_token);
          if (resp.refresh_token) localStorage.setItem('refresh_token', resp.refresh_token);
          if (userId != null) localStorage.setItem('user_id', userId.toString());
          localStorage.setItem('user_email', userEmail);
          if (resp.user?.name) localStorage.setItem('user_name', resp.user.name);
          this.accessTokenSignal.set(resp.access_token);
          this.emailSignal.set(userEmail);
          this.userIdSignal.set(userId != null ? userId.toString() : null);
          if (userId != null) this.profilePersonalData.loadUserById(userId.toString());

        }),
        finalize(() => {
          this.spinner.close();
        }),
      );
  }

  logout(): Observable<{ message: string }> {
    const request = this.http
      .post<{ message: string }>(`${this.API_URL}/backoffice/logout`, {})
      .pipe(finalize(() => this.clearSession()));
    return request;
  }

  clearSession() {
    localStorage.clear();
    this.backofficeAccess.clearAccess();
    this.accessTokenSignal.set(null);
    this.emailSignal.set(null);
    this.userIdSignal.set(null);
  }

  isLoggedIn(): boolean {
    return !!this.accessTokenSignal();
  }

  getToken(): string | null {
    return this.accessTokenSignal();
  }

  getEmail(): string | null {
    return this.emailSignal();
  }

  getUser_id(): string | null {
    return this.userIdSignal();
  }
}
